// ---------------------------------------------------------------------------
// Box-change analytics: plan-size changes and add-on add/remove/quantity.
// ---------------------------------------------------------------------------
// Feeds the redesign A/B analysis (Website Insights) with the two things the
// order-level numbers can't separate on their own:
//
//   • plan_size  – every committed size/plan-type change, with direction and
//                  the surface that triggered it (size selector, upgrade
//                  offer, auto-select from an upgrade modal).
//   • addon      – every add-on create / delete / quantity change, with the
//                  line value and the surface it happened on.
//
// Both carry the same session context so the dashboard can split first-visit
// behaviour (before the first charge) from steady-state editing:
//
//   session_id               – per-tab id, groups edits made in one sitting
//   is_first_portal_session  – first time THIS device has seen this member
//                              (device-scoped; combine with the two below)
//   days_since_signup        – from Recharge customer.created_at
//   before_first_charge      – customer.first_charge_processed_at is unset
//
// Everything is fire-and-forget through recordUserEvent (api.js), which also
// stamps redesign bucket + active version. Nothing here may throw into a
// member-facing flow.
//
// Add-on events are emitted from the recharge.js onetime mutations (the one
// choke point every surface — builder, overview picker, product modal, deals
// modal, curated bar — goes through), so this module keeps a small registry
// of onetime rows it has seen: deletes and quantity updates only carry an id,
// and the registry is how they get their product, price and previous quantity
// back. recharge.js feeds it from fetchOnetimes / create / update results.

import { recordUserEvent } from './api';

const SESSION_ID_KEY = 'wg_portal_session_id';
const FIRST_SESSION_KEY = 'wg_portal_first_session';
const FIRST_SEEN_PREFIX = 'wg_portal_first_seen:';

export const BOX_CHANGE_EVENT = 'box_change';
export const BOX_CHANGE_STEP = { PLAN_SIZE: 'plan_size', ADDON: 'addon' };

const safeStorage = (storage, fn) => {
  try {
    return fn(storage);
  } catch {
    return null;
  }
};

const str = (v) => (v === undefined || v === null ? null : String(v));
const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : null;
};
const round2 = (n) => (n === null ? null : Math.round(n * 100) / 100);

// ---------------------------------------------------------------------------
// Member / subscription context (fed by recharge.js reads)
// ---------------------------------------------------------------------------
let customer = null;
// address_id -> { id, next_charge_scheduled_at }. Onetimes hang off an
// address, not a subscription, so this is how an add-on event finds the
// membership it belongs to.
const subscriptionsByAddress = new Map();

export const rememberCustomer = (c) => {
  if (!c || typeof c !== 'object') return;
  customer = {
    id: str(c.id),
    email: c.email || customer?.email || null,
    created_at: c.created_at || customer?.created_at || null,
    first_charge_processed_at:
      c.first_charge_processed_at !== undefined
        ? c.first_charge_processed_at
        : customer?.first_charge_processed_at ?? undefined,
  };
  markFirstSession(customer.id);
};

export const rememberSubscriptions = (subs) => {
  (Array.isArray(subs) ? subs : [subs]).forEach((sub) => {
    if (!sub || typeof sub !== 'object') return;
    if (sub.include?.customer) rememberCustomer(sub.include.customer);
    const addressId = str(sub.address_id || sub.include?.address?.id);
    if (!addressId || !sub.id) return;
    // Prefer an active box over anything cancelled at the same address.
    const existing = subscriptionsByAddress.get(addressId);
    if (existing && existing.status === 'active' && sub.status !== 'active') return;
    subscriptionsByAddress.set(addressId, {
      id: str(sub.id),
      status: sub.status || null,
      next_charge_scheduled_at: sub.next_charge_scheduled_at || null,
    });
  });
};

// ---------------------------------------------------------------------------
// Onetime registry (fed by recharge.js reads + mutation results)
// ---------------------------------------------------------------------------
const onetimeRegistry = new Map();

const onetimeRow = (result) => (result && (result.onetime || result)) || null;

const isTierBonusRow = (row) =>
  Array.isArray(row?.properties) && row.properties.some((p) => p?.name === '_tier_bonus');

export const rememberOnetime = (result) => {
  const row = onetimeRow(result);
  if (!row?.id) return null;
  const entry = {
    id: str(row.id),
    address_id: str(row.address_id),
    product_id: str(row.external_product_id?.ecommerce),
    variant_id: str(row.external_variant_id?.ecommerce),
    sku: row.sku || null,
    title: row.product_title || null,
    price: num(row.price),
    quantity: Number(row.quantity) || 0,
    is_tier_bonus: isTierBonusRow(row),
  };
  onetimeRegistry.set(entry.id, entry);
  return entry;
};

export const rememberOnetimes = (rows) => {
  if (!Array.isArray(rows)) return;
  rows.forEach(rememberOnetime);
};

export const forgetOnetime = (onetimeId) => {
  onetimeRegistry.delete(str(onetimeId));
};

export const getRememberedOnetime = (onetimeId) => onetimeRegistry.get(str(onetimeId)) || null;

// ---------------------------------------------------------------------------
// Session context
// ---------------------------------------------------------------------------
const getSessionId = () =>
  safeStorage(sessionStorage, (s) => {
    let id = s.getItem(SESSION_ID_KEY);
    if (!id) {
      id = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
      s.setItem(SESSION_ID_KEY, id);
    }
    return id;
  });

// First time this device sees this member → flag the whole tab session as
// their first portal session. Device-scoped by nature (a member on a second
// device reads as "first" again), which is why days_since_signup and
// before_first_charge ride along on every event.
const markFirstSession = (customerId) => {
  if (!customerId) return;
  const key = `${FIRST_SEEN_PREFIX}${customerId}`;
  const alreadySeen = safeStorage(localStorage, (s) => s.getItem(key));
  if (alreadySeen) return;
  safeStorage(localStorage, (s) => s.setItem(key, new Date().toISOString()));
  safeStorage(sessionStorage, (s) => s.setItem(FIRST_SESSION_KEY, 'true'));
};

// Exported for the box builder, which leads first-session members with the
// Top rated chapter (same device-scoped signal the events carry).
export const isFirstPortalSession = () =>
  safeStorage(sessionStorage, (s) => s.getItem(FIRST_SESSION_KEY)) === 'true';

const daysSinceSignup = () => {
  const createdAt = customer?.created_at ? new Date(customer.created_at).getTime() : NaN;
  if (!Number.isFinite(createdAt)) return null;
  return Math.max(0, Math.floor((Date.now() - createdAt) / 86_400_000));
};

// null when Recharge didn't hand us the field (older customer includes), so
// downstream can tell "unknown" from "charged".
const beforeFirstCharge = () => {
  if (!customer || customer.first_charge_processed_at === undefined) return null;
  return !customer.first_charge_processed_at;
};

// Which page / popup the change happened on. Popups mirror themselves into
// ?modal= (usePopupUrlSync), so this is enough to split e.g. the box builder
// from the overview add-on picker or the post-save deals modal.
const currentSurface = () => {
  if (typeof window === 'undefined') return null;
  const path = window.location.pathname.replace(/\/\d+(?=\/|$)/g, '/:id');
  const modal = new URLSearchParams(window.location.search).get('modal');
  return modal ? `${path}#${modal}` : path;
};

const sessionContext = () => ({
  session_id: getSessionId(),
  is_first_portal_session: isFirstPortalSession(),
  days_since_signup: daysSinceSignup(),
  before_first_charge: beforeFirstCharge(),
  surface: currentSurface(),
});

const emit = ({ email, subscriptionId, step, action, properties }) => {
  try {
    recordUserEvent({
      email: email || customer?.email || null,
      subscriptionId: subscriptionId || null,
      eventName: BOX_CHANGE_EVENT,
      step,
      action,
      properties: { ...sessionContext(), ...properties },
    });
  } catch (err) {
    console.warn('box change event failed', step, action, err?.message || err);
  }
};

// ---------------------------------------------------------------------------
// Plan size / plan type change (committed on box save)
// ---------------------------------------------------------------------------
/**
 * @param {Object} p
 * @param {string}  [p.email]
 * @param {string}  [p.subscriptionId]
 * @param {string}  [p.nextChargeDate]
 * @param {number}  p.fromItems      – slot count before (4/6/8/12)
 * @param {number}  p.toItems        – slot count after
 * @param {string}  [p.fromVariantId] – Shopify variant id before
 * @param {string}  [p.toVariantId]   – Shopify variant id after
 * @param {string}  [p.fromTitle]     – variant title before ("6 items / Regular")
 * @param {string}  [p.toTitle]
 * @param {number}  [p.fromPrice]     – box price before
 * @param {number}  [p.toPrice]
 * @param {string}  [p.source]        – size_selector | upgrade_offer | auto_select
 */
export const trackPlanSizeChange = ({
  email,
  subscriptionId,
  nextChargeDate,
  fromItems,
  toItems,
  fromVariantId,
  toVariantId,
  fromTitle,
  toTitle,
  fromPrice,
  toPrice,
  source,
} = {}) => {
  const from = Number(fromItems) || null;
  const to = Number(toItems) || null;
  const action = from && to && to !== from ? (to > from ? 'upgrade' : 'downgrade') : 'plan_type';
  const fromP = num(fromPrice);
  const toP = num(toPrice);
  emit({
    email,
    subscriptionId: str(subscriptionId),
    step: BOX_CHANGE_STEP.PLAN_SIZE,
    action,
    properties: {
      source: source || 'unknown',
      from_items: from,
      to_items: to,
      from_variant_id: str(fromVariantId),
      to_variant_id: str(toVariantId),
      from_title: fromTitle || null,
      to_title: toTitle || null,
      from_price: round2(fromP),
      to_price: round2(toP),
      price_delta: fromP !== null && toP !== null ? round2(toP - fromP) : null,
      next_charge_date: nextChargeDate || null,
    },
  });
};

// ---------------------------------------------------------------------------
// Add-on add / remove / quantity (emitted from recharge.js onetime mutations)
// ---------------------------------------------------------------------------
const addonProperties = (entry, { quantityFrom = null } = {}) => {
  const sub = entry.address_id ? subscriptionsByAddress.get(entry.address_id) : null;
  const price = entry.price;
  const qty = entry.quantity;
  return {
    subscriptionId: sub?.id || null,
    properties: {
      onetime_id: entry.id,
      product_id: entry.product_id,
      variant_id: entry.variant_id,
      sku: entry.sku,
      title: entry.title,
      price: round2(price),
      quantity: qty,
      quantity_from: quantityFrom,
      line_value: price !== null ? round2(price * qty) : null,
      line_value_delta:
        price !== null && quantityFrom !== null ? round2(price * (qty - quantityFrom)) : null,
      is_tier_bonus: entry.is_tier_bonus,
      next_charge_date: sub?.next_charge_scheduled_at || null,
    },
  };
};

/**
 * A new onetime row was created. `result` is the create response (either the
 * bare row or `{ onetime }`); `productData` is the caller's product payload,
 * used to fill in sku/price when the response lacks them.
 */
export const trackAddonAdded = (result, productData = null) => {
  const entry = rememberOnetime(result);
  if (!entry) return;
  const variant = productData?.variants?.[0];
  if (entry.sku === null && variant?.sku) entry.sku = variant.sku;
  if (entry.title === null && productData?.title) entry.title = productData.title;
  if (entry.price === null && !entry.is_tier_bonus) {
    entry.price = num(variant?.price?.amount ?? variant?.price ?? productData?.price);
  }
  const { subscriptionId, properties } = addonProperties(entry, { quantityFrom: 0 });
  emit({ subscriptionId, step: BOX_CHANGE_STEP.ADDON, action: 'add', properties });
};

/** A onetime row was deleted. Only emits when the row is known. */
export const trackAddonRemoved = (onetimeId) => {
  const entry = getRememberedOnetime(onetimeId);
  forgetOnetime(onetimeId);
  if (!entry) return;
  const { subscriptionId, properties } = addonProperties(
    { ...entry, quantity: 0 },
    { quantityFrom: entry.quantity }
  );
  emit({ subscriptionId, step: BOX_CHANGE_STEP.ADDON, action: 'remove', properties });
};

/**
 * A onetime row was updated. Emits only when the quantity actually moved —
 * reschedules (next_charge_scheduled_at only) are not add-on changes.
 */
export const trackAddonQuantityChanged = (onetimeId, updateData = {}, result = null) => {
  const before = getRememberedOnetime(onetimeId);
  const after = rememberOnetime(result) || (before
    ? { ...before, quantity: Number(updateData.quantity ?? before.quantity) || 0 }
    : null);
  if (!after) return;
  if (updateData.quantity === undefined) return;
  const quantityFrom = before ? before.quantity : null;
  if (quantityFrom !== null && quantityFrom === after.quantity) return;
  onetimeRegistry.set(after.id, after);
  const { subscriptionId, properties } = addonProperties(after, { quantityFrom });
  emit({ subscriptionId, step: BOX_CHANGE_STEP.ADDON, action: 'quantity', properties });
};
